<script lang="ts">
  import { MAX_LENGTH, type ChatMessageAdmin } from '@pin/schemas/socket';
  import { tick } from 'svelte';
  import {
    banUser,
    clearChat,
    redactTerm,
    sendAdminMessage,
    unBanUser,
  } from '../../api/api';
  import { addBlocklistItem, blocklistSubject$ } from '../../api/blocklist.api';
  import { adminName } from '../../stores/configStore';
  import { addToast } from '../../stores/toastStore';
  import { parseDurationToSeconds } from '../../utils/regex';
  import EmotePicker from './EmotePicker.svelte';

  interface Props {
    chatMessages: ChatMessageAdmin[];
    selectedUser: ChatMessageAdmin['user'] | null;
  }
  let { chatMessages, selectedUser = $bindable() }: Props = $props();

  let message = $state('');
  let textArea = $state<HTMLTextAreaElement>();

  type Command = { command: string; description: string };
  let commandsToDisplay = $state<Command[]>([]);
  let selectCommandIndex = $state(0);
  let commandPopup = $state<HTMLDivElement>();

  let usersToDisplay = $state<ChatMessageAdmin['user'][]>([]);
  let selectUserIndex = $state(0);
  let userPopup = $state<HTMLDivElement>();

  const commands: Command[] = [
    {
      command: '/clear',
      description: 'Clear the chat for messages. Usage: /clear',
    },
    {
      command: '/nuke ',
      description: `Redacts messages containing a term or phrase optionally banning the users. Usage: /nuke <text> ([s|m|h]|inf). inf bans for 90 days, the longest ban. Omit the ending parenthesis for no ban`,
    },
    {
      command: '/timeout ',
      description: 'Timeout a user. Usage: /timeout <username> <seconds>',
    },
    {
      command: '/ban ',
      description: 'Ban a user from chat for 90 days. Usage: /ban <username>',
    },
    {
      command: '/untimeout ',
      description: 'Untimeout a user. Usage: /untimeout <username>',
    },
    {
      command: '/unban ',
      description: 'Unban a user from chat. Usage: /unban <username>',
    },
    {
      command: '/blocklist add ',
      description:
        'Add a word or phrase to the blocklist and nukes it. Usage: /blocklist add <text>',
    },
  ];

  const handeTextInput = (
    event: Event & { currentTarget: HTMLTextAreaElement }
  ) => {
    const value = event.currentTarget.value.trimStart();
    const [cmd, ...args] = value.split(' ');

    commandsToDisplay = [];
    usersToDisplay = [];

    if (!value.startsWith('/')) {
      selectedUser = null;
      return;
    }

    const cmdName = cmd.toLowerCase().slice(1);
    if (args.length === 0) {
      commandsToDisplay = commands.filter((c) =>
        c.command.toLowerCase().startsWith('/' + cmdName)
      );
      return;
    }

    const foundCmd = commands.find((c) =>
      c.command.toLowerCase().startsWith('/' + cmdName)
    );
    if (args.length !== 0 && foundCmd) {
      const usernameToFind = args.join(' ').toLowerCase();
      if (
        foundCmd.description.includes('<username>') &&
        usernameToFind.length > 0
      ) {
        // Built here rather than passed in, so it is not rebuilt on every
        // message
        const users = new Map(chatMessages.map(({ user }) => [user.id, user]));
        usersToDisplay = Array.from(users.values())
          .filter((u) => u.name.toLowerCase().startsWith(usernameToFind))
          .sort((a, b) => a.name.localeCompare(b.name));
        selectedUser = usersToDisplay[selectUserIndex];
      }
    }
  };

  const selectCommand = (cmd: { command: string }) => {
    message = cmd.command;
    commandsToDisplay = [];
  };

  const selectUser = (user: ChatMessageAdmin['user']) => {
    const [cmd] = message.trim().split(' ');
    message = cmd + ' ' + user.name;
    usersToDisplay = [];
  };

  const handleKeyDown = (
    e: KeyboardEvent & {
      currentTarget: EventTarget & HTMLTextAreaElement;
    }
  ) => {
    if (commandsToDisplay.length > 0) {
      return handleCommandPopupKeyDown(e);
    }
    if (usersToDisplay.length > 0) {
      return handleUserPopupKeyDown(e);
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      handleMessageSend();
    }
  };

  const handleCommandPopupKeyDown = async (
    e: KeyboardEvent & {
      currentTarget: EventTarget & HTMLTextAreaElement;
    }
  ) => {
    switch (e.key) {
      case 'ArrowDown': {
        e.preventDefault();
        selectCommandIndex =
          (selectCommandIndex + 1) % commandsToDisplay.length;
        break;
      }
      case 'ArrowUp': {
        e.preventDefault();
        selectCommandIndex =
          (selectCommandIndex - 1 + commandsToDisplay.length) %
          commandsToDisplay.length;
        break;
      }
      case 'Enter': {
        e.preventDefault();
        selectCommand(commandsToDisplay[selectCommandIndex]);
        break;
      }
    }
    await tick();
    commandPopup
      ?.querySelector<HTMLButtonElement>('button.selected')
      ?.scrollIntoView({ block: 'nearest' });
  };

  const handleUserPopupKeyDown = async (
    e: KeyboardEvent & {
      currentTarget: EventTarget & HTMLTextAreaElement;
    }
  ) => {
    switch (e.key) {
      case 'ArrowDown': {
        break;
      }
      case 'ArrowUp': {
        break;
      }
      case 'Enter': {
        e.preventDefault();
        selectedUser = usersToDisplay[selectUserIndex];
        selectUser(usersToDisplay[selectUserIndex]);
      }
    }
    await tick();
    userPopup
      ?.querySelector<HTMLButtonElement>('button.selected')
      ?.scrollIntoView({ block: 'nearest' });
  };

  const handleMessageSend = async () => {
    const trimmed = message.trim();
    if (trimmed.length === 0) {
      return;
    }
    if (trimmed.startsWith('/')) {
      const [cmd, ...args] = trimmed.split(' ');
      switch (cmd.toLowerCase()) {
        case '/clear': {
          try {
            await clearChat({
              issuedBy: $adminName,
            });
            addToast({ message: 'Chat was cleared' });
          } catch (e) {
            addToast({ message: `Couldn't clear chat: ${String(e)}` });
          }
          break;
        }
        case '/nuke': {
          if (args.length === 0) {
            return addToast({
              message:
                'Invalid usage of nuke command, please see the help text',
              type: 'error',
            });
          }

          const input = args.join(' ');
          const match = input.match(
            /^(?<term>.*?)(?:\s*\((?<durationStr>[^)]+)\))?$/
          );
          const term = match?.groups?.term;
          const durationStr = match?.groups?.durationStr;

          if (term == null) {
            return addToast({
              message: 'Nuke requires a term or phrase to nuke',
              type: 'error',
            });
          }

          let durationS: number | null | undefined = undefined;
          if (durationStr != null) {
            if (durationStr.toLowerCase() === 'inf') {
              durationS = null;
            } else {
              const result = parseDurationToSeconds(durationStr);
              if (!result.ok) {
                return addToast({ message: result.error, type: 'error' });
              }
              durationS = result.duration;
            }
          }
          try {
            await redactTerm({
              term,
              issuedBy: $adminName,
              durationS,
            });
            addToast({ message: `'${term}' was nuked` });
          } catch (e) {
            addToast({ message: `Couldn't nuke '${term}': ${String(e)}` });
          }
          break;
        }
        case '/timeout': {
          if (selectedUser !== null) {
            const durationStr = args[args.length - 1] ?? '';
            const result = parseDurationToSeconds(durationStr);
            if (!result.ok) {
              return addToast({ message: result.error, type: 'error' });
            }
            try {
              await banUser({
                userId: selectedUser.id,
                viaUserId: selectedUser.viaUserId,
                issuedBy: $adminName,
                durationS: result.duration,
              });
              addToast({
                message: `'${selectedUser.name}' was timed out (${durationStr})`,
              });
            } catch (e) {
              addToast({
                message: `Couldn't timeout '${selectedUser.name}': ${String(e)}`,
                type: 'error',
              });
            }
          }
          break;
        }
        case '/ban': {
          if (selectedUser !== null) {
            try {
              await banUser({
                userId: selectedUser.id,
                viaUserId: selectedUser.viaUserId,
                issuedBy: $adminName,
                durationS: null,
              });
              addToast({ message: `'${selectedUser.name}' was banned` });
            } catch (e) {
              addToast({
                message: `Couldn't ban '${selectedUser.name}': ${String(e)}`,
                type: 'error',
              });
            }
          }
          break;
        }
        case '/untimeout':
        case '/unban': {
          if (selectedUser !== null) {
            try {
              await unBanUser({
                userId: selectedUser.id,
                viaUserId: selectedUser.viaUserId,
                issuedBy: $adminName,
              });
              addToast({ message: `'${selectedUser.name}' was unbanned` });
            } catch (e) {
              addToast({
                message: `Couldn't unban '${selectedUser.name}': ${String(e)}`,
                type: 'error',
              });
            }
          }
          break;
        }
        case '/blocklist': {
          const subCmd = args[0]?.toLowerCase();
          if (subCmd === 'add') {
            const text = args.slice(1).join(' ').trim();
            if (!text) {
              return addToast({
                message: 'Blocklist add requires text to block',
                type: 'error',
              });
            }
            try {
              await addBlocklistItem({ text });
              blocklistSubject$.next();
              addToast({ message: `Blocked word '${text}' added` });
            } catch (e) {
              addToast({
                message: `Couldn't add blocked word '${text}': ${String(e)}`,
                type: 'error',
              });
            }
            try {
              await redactTerm({
                term: text,
                issuedBy: $adminName,
              });
              addToast({ message: `'${text}' was nuked` });
            } catch (e) {
              addToast({ message: `Couldn't nuke '${text}': ${String(e)}` });
            }
          } else {
            return addToast({
              message:
                'Invalid or missing blocklist subcommand. Use: /blocklist add <text>',
              type: 'error',
            });
          }
          break;
        }
      }
    } else {
      try {
        await sendAdminMessage({ text: trimmed, issuedBy: $adminName });
      } catch (e) {
        addToast({
          message: `Couldn't send admin message: ${String(e)}`,
          type: 'error',
        });
      }
    }

    message = '';
    selectedUser = null;
    commandsToDisplay = [];
    usersToDisplay = [];
    selectCommandIndex = 0;
    selectUserIndex = 0;
  };
</script>

<form
  class="send-container"
  onsubmit={(e) => {
    e.preventDefault();
    handleMessageSend();
  }}
>
  <div class="textarea-wrapper">
    <textarea
      name="writeMessage"
      placeholder={$adminName?.length === 0
        ? 'Choose username'
        : `Enter message as '${$adminName}'...`}
      autocomplete="off"
      rows="1"
      maxlength={MAX_LENGTH}
      bind:this={textArea}
      bind:value={message}
      disabled={$adminName?.length === 0}
      oninput={handeTextInput}
      onkeydown={handleKeyDown}></textarea>
    <EmotePicker
      input={textArea}
      bind:value={message}
      disabled={$adminName?.length === 0}
    />

    {#if commandsToDisplay.length !== 0}
      <div class="command-popup" bind:this={commandPopup}>
        {#each commandsToDisplay as cmd, index (cmd.command)}
          <button
            class:selected={index === selectCommandIndex}
            onclick={() => selectCommand(commandsToDisplay[selectCommandIndex])}
          >
            <strong>{cmd.command}</strong> - {cmd.description}
          </button>
        {/each}
      </div>
    {/if}

    {#if usersToDisplay.length}
      <div class="command-popup" bind:this={userPopup}>
        {#each usersToDisplay as user, index (user.id)}
          <button
            class:selected={index === selectUserIndex}
            onclick={() => selectUser(user)}
          >
            {user.name} <small>({user.id})</small>
          </button>
        {/each}
      </div>
    {/if}
  </div>
  <button class="button" type="submit">Send</button>
</form>

<style>
  form {
    margin: 0;
    padding: 0;
  }

  .textarea-wrapper {
    display: flex;
    width: 100%;
    position: relative;
  }

  .send-container {
    display: flex;
    align-items: center;
    width: 100%;
    gap: 0.5rem;
  }

  textarea {
    height: 3rem;
    padding: 0.5rem;
    padding-right: 2.5rem;
    border: 1px solid var(--color-zinc-600);
    border-radius: 0.5rem;
    background-color: transparent;
    color: var(--color-zinc-200);
    font-size: 1rem;
    width: 100%;
    resize: none;
    transition: border-color 0.2s ease;
  }

  textarea:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  .button {
    display: flex;
    gap: 0.25rem;
    padding: 0.5rem 1rem;
    background-color: var(--color-zinc-700);
    border-radius: 0.5rem;
    color: var(--color-zinc-200);
    border: 1px solid var(--color-zinc-600);
    font-size: 1rem;
    cursor: pointer;
    transition: background-color 0.2s ease;
  }

  .button:hover {
    background-color: var(--color-zinc-600);
  }

  .button:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  .button:focus {
    outline: none;
    border-color: var(--color-zinc-400);
    border-width: 1px;
    border-style: solid;
    border-radius: 0.5rem;
  }

  .command-popup {
    position: absolute;
    bottom: 3rem;
    left: 0;
    background-color: var(--color-zinc-600);
    border: 1px solid #555;
    border-radius: 0.5rem;
    max-height: 200px;
    overflow-y: auto;
    width: 100%;
    z-index: 10;
    padding: 0.25rem 0;
  }
  .command-popup button {
    all: unset;
    padding: 0.5rem 0;
    text-align: left;
    width: 100%;
  }
  .command-popup button.selected,
  .command-popup button:hover {
    background-color: var(--color-sky-400);
    color: var(--color-sky-50);
  }
</style>
